/**
 * The device form in a modal.
 *
 * Thin: the form is `LabDeviceForm`, because "+ New device" is offered from inside the item form and
 * from the builder tree, and both of those open it in a slide-over rather than navigating away. What
 * stays here is the draft, the save, and the errors.
 *
 * Two saves, not one. The device is a row in the library and its sections are rows in the link table,
 * so this writes the device and then reconciles the attachments against what the form was handed.
 * They are not one request: the generic CRUD API takes one table at a time, and the device has to
 * exist before anything can point at it. A failure between them leaves the device saved and the
 * attachments as they were, which is reported rather than hidden - the box is real either way, and
 * re-opening the form shows exactly which sections it is in.
 */

import React, { useEffect, useMemo, useState } from 'react';
import StbModal from '../security-toolbox-ui/StbModal';
import {
  useLabDevices,
  useLabDeviceTypes,
  useLabProducts,
  useLabScenarios,
  useSaveLabRow,
  useSyncLabDeviceScenarios,
} from '../../hooks/lab-hooks';
import { LabDevice, LabScenario, LAB_TABLES } from '../../support/labService';
import { labErrorMessage } from './LabShared';
import LabDeviceForm, { labDeviceEmpty, labDeviceFormBlockers } from './LabDeviceForm';

export type LabDeviceEditorModalProps = {
  open: boolean;
  /**
   * The section the modal was opened from, when there was one.
   *
   * Not ownership - a device has no scenario. It seeds the ordering and starts that section ticked
   * in the attachment list, so opening "+ New device" from inside a section still ends with the box
   * attached to it. Null from the Devices library, and then nothing is pre-ticked.
   */
  scenario: LabScenario | null;
  device: LabDevice | null;
  onClose: () => void;
  onSaved?: (device: LabDevice) => void;
};

const LabDeviceEditorModal: React.FC<LabDeviceEditorModalProps> = ({
  open,
  scenario,
  device,
  onClose,
  onSaved,
}) => {
  const [draft, setDraft] = useState<Partial<LabDevice>>(() => labDeviceEmpty(scenario));
  const [attachments, setAttachments] = useState<number[]>([]);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSeed, setFormSeed] = useState(0);

  const save = useSaveLabRow<Partial<LabDevice> & { id?: number }>(LAB_TABLES.device);
  const syncSections = useSyncLabDeviceScenarios();

  const { data: deviceTypeRows } = useLabDeviceTypes({}, { enabled: open });
  const deviceTypes = useMemo(() => deviceTypeRows || [], [deviceTypeRows]);

  /**
   * Every section, always - not only when the caller failed to name one.
   *
   * The list is the attachment editor now rather than a picker of last resort, so it is needed even
   * when the modal was opened from inside a section: that section is one tick among several, and the
   * others are how a device authored once ends up in every scenario that uses the same pod.
   */
  const { data: scenarioRows } = useLabScenarios({}, { enabled: open });
  const scenarios = useMemo(() => scenarioRows || [], [scenarioRows]);

  /** The library, for the jump-host picker: any device may be the route to any other. */
  const { data: deviceRows } = useLabDevices({}, { enabled: open });
  const libraryDevices = useMemo(() => deviceRows || [], [deviceRows]);

  const chosenType = useMemo(
    () => deviceTypes.find((candidate) => candidate.id === draft.device_type) || null,
    [deviceTypes, draft.device_type],
  );

  /**
   * Two thousand products, fetched only once a family type is chosen.
   *
   * Most devices never ask the question - a type that is one product answers it for them - so the
   * catalogue is pulled when the second select is actually about to render.
   */
  const { data: productRows, isLoading: productsLoading } = useLabProducts({
    enabled: open && !!chosenType?.requires_product,
  });
  const products = useMemo(() => productRows || [], [productRows]);

  useEffect(() => {
    if (!open) return;

    setDraft(device ? { ...device } : labDeviceEmpty(scenario));
    // An existing device's sections come from its own link rows; a new one starts in the section it
    // was opened from, if any. Neither is a default anybody has to accept - the list is editable.
    setAttachments(
      device
        ? (device.scenario_links || []).map((link) => link.scenario)
        : scenario
        ? [scenario.id]
        : [],
    );
    setJsonError(null);
    setFormError(null);
    setFormSeed((seed) => seed + 1);
    save.reset();
    syncSections.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, device?.id, scenario?.id]);

  const set = (patch: Partial<LabDevice>) => {
    setFormError(null);
    setDraft((current) => ({ ...current, ...patch }));
  };

  const saveBlockers = useMemo(
    () => labDeviceFormBlockers(draft, { deviceType: chosenType }),
    [draft, chosenType],
  );

  const handleSubmit = async () => {
    if (jsonError) {
      setFormError(`Attributes: ${jsonError}`);
      return;
    }

    if (saveBlockers.length) {
      setFormError(saveBlockers[0]);
      return;
    }

    /**
     * The key is derived from the name now, so a clash is a name collision.
     *
     * Worth catching here rather than letting the unique constraint do it: the database refuses with
     * "UNIQUE constraint failed: lab_device.device_key", which says nothing about which of the two
     * hundred devices already holds it. And the answer is usually "attach that one" rather than
     * "rename this one" - two sections wanting the same box is what the library is for.
     */
    const clash = libraryDevices.find(
      (candidate) => candidate.id !== draft.id && candidate.device_key === draft.device_key,
    );

    if (clash) {
      setFormError(
        `${clash.display_name || clash.device_key} already uses the key ${
          draft.device_key
        }. If that is the same box, attach it to this section instead of creating a second one - a device is a library entry and belongs to as many sections as use it.`,
      );
      return;
    }

    setFormError(null);

    let saved: LabDevice;

    try {
      saved = (await save.mutateAsync(draft)) as LabDevice;
    } catch {
      // Surfaced through save.error.
      return;
    }

    try {
      await syncSections.mutateAsync({
        deviceId: saved.id,
        existing: device?.scenario_links || [],
        scenarioIds: attachments,
      });
    } catch (error) {
      // The device is saved. Said plainly rather than swallowed, because closing here would leave an
      // admin believing the sections took - and left open, the list still shows what is true.
      setFormError(
        `${saved.device_key} was saved, but its sections were not updated: ${labErrorMessage(
          error,
        )}`,
      );
      return;
    }

    onSaved?.(saved);
    onClose();
  };

  const submitError = formError || (save.isError ? labErrorMessage(save.error) : null);
  const busy = save.isPending || syncSections.isPending;

  return (
    <StbModal
      open={open}
      role="update"
      title={
        device
          ? `Edit device ${device.device_key}`
          : scenario
          ? `New device, attached to ${scenario.name}`
          : 'New device in the library'
      }
      size="large"
      scrollable
      submitLabel={device ? 'Save device' : 'Create device'}
      submitDisabled={!!saveBlockers.length || !!jsonError || busy}
      suppressCloseOnSubmit
      onSubmit={handleSubmit}
      onClose={async () => onClose()}
      apiResponseSection={
        submitError ? (
          <p className="m-0 text-sm text-amber-800">{submitError}</p>
        ) : saveBlockers.length ? (
          <ul className="m-0 list-disc pl-5 text-sm text-gray-600">
            {saveBlockers.map((blocker) => (
              <li key={blocker}>{blocker}</li>
            ))}
          </ul>
        ) : undefined
      }
    >
      <LabDeviceForm
        scenario={scenario}
        scenarios={scenarios}
        device={device}
        draft={draft}
        onChange={set}
        deviceTypes={deviceTypes}
        attachments={attachments}
        onAttachmentsChange={setAttachments}
        products={products}
        productsLoading={productsLoading}
        libraryDevices={libraryDevices}
        jsonError={jsonError}
        onJsonError={setJsonError}
        resetKey={formSeed}
      />
    </StbModal>
  );
};

export default LabDeviceEditorModal;
